import { useEffect, useState } from "react";

// Owns the app's ONLY service worker registration (replacing the bare
// `navigator.serviceWorker.register()` call that used to live in
// index.js) so registration and update-detection share one source of
// truth. Renders nothing until a genuine new version is available.
//
// Why this exists: public/sw.js already calls skipWaiting() + clients.
// claim() on every install/activate, so a new deploy's service worker
// takes over almost immediately — but "takes over" only changes which
// worker answers this page's FUTURE network requests. It does nothing to
// the React bundle already sitting in the browser's memory, and browsers
// only check for a new sw.js automatically on a fresh navigation. A field
// pumper who leaves this tab open all day (the normal pattern — see
// PumperRoute.js) would otherwise never see today's deploy until they
// happen to fully close and reopen the app. This component closes that
// gap: it polls for updates while the tab is open/visible, and once a new
// worker has genuinely taken control of an ALREADY-ACTIVE page (not this
// origin's first-ever visit — see hadControllerAlready below), it shows a
// small reload prompt instead of forcing a reload, so an in-progress
// pumper report never gets silently wiped out from under someone.
export default function UpdateBanner() {
  const [updateAvailable, setUpdateAvailable] = useState(false);

  useEffect(() => {
    if (!("serviceWorker" in navigator)) return undefined;

    let cancelled = false;
    let pollId = null;
    let visibilityHandler = null;

    // Checked BEFORE registering — a controller already existing at this
    // moment means this is a repeat visit with a service worker from a
    // previous session already active, so any update this effect detects
    // from here on is a genuine new deploy. On a brand-new origin (no
    // service worker ever registered before), there is no controller yet
    // at this point, and `clients.claim()` taking effect for the very
    // first time must NOT be mistaken for an update.
    const hadControllerAlready = !!navigator.serviceWorker.controller;

    function watchInstallingWorker(installingWorker) {
      if (!installingWorker) return;
      installingWorker.addEventListener("statechange", () => {
        if (cancelled) return;
        if (installingWorker.state === "installed" && hadControllerAlready) {
          setUpdateAvailable(true);
        }
      });
    }

    function registerAndWatch() {
      navigator.serviceWorker
        .register("/sw.js")
        .then((reg) => {
          if (cancelled) return;
          console.log("RigIQ Service Worker registered:", reg.scope);

          if (reg.installing) watchInstallingWorker(reg.installing);
          reg.addEventListener("updatefound", () => watchInstallingWorker(reg.installing));

          // Browsers only auto-check for a new sw.js on navigation, so a
          // long-lived open tab needs its own poll to ever discover a
          // same-day deploy. reg.update() is a no-op (network 304/cached)
          // when nothing changed, so this is cheap to run often.
          const checkForUpdate = () => reg.update().catch(() => {});
          pollId = setInterval(checkForUpdate, 5 * 60 * 1000);
          visibilityHandler = () => {
            if (document.visibilityState === "visible") checkForUpdate();
          };
          document.addEventListener("visibilitychange", visibilityHandler);
        })
        .catch((err) => {
          console.log("Service Worker registration failed:", err);
        });
    }

    // Deferred to window "load" (matches the original registration site in
    // index.js) rather than registered immediately — this is a field app
    // used on phones, often on weak cellular signal, so registration
    // shouldn't compete with the page's own critical first-load resources.
    if (document.readyState === "complete") {
      registerAndWatch();
    } else {
      window.addEventListener("load", registerAndWatch);
    }

    return () => {
      cancelled = true;
      window.removeEventListener("load", registerAndWatch);
      if (pollId) clearInterval(pollId);
      if (visibilityHandler) document.removeEventListener("visibilitychange", visibilityHandler);
    };
  }, []);

  if (!updateAvailable) return null;

  return (
    <div
      style={{
        position: "fixed",
        bottom: "20px",
        left: "20px",
        zIndex: 9999,
        display: "flex",
        alignItems: "center",
        gap: "10px",
        padding: "10px 16px",
        borderRadius: "8px",
        backgroundColor: "rgba(0,245,255,0.1)",
        border: "1px solid rgba(0,245,255,0.3)",
        backdropFilter: "blur(10px)",
        boxShadow: "0 4px 20px rgba(0,0,0,0.3)",
      }}
    >
      <span style={{ color: "#00f5ff", fontSize: "11px", fontWeight: "700", letterSpacing: "1px" }}>
        NEW VERSION AVAILABLE
      </span>
      <button
        onClick={() => window.location.reload()}
        style={{
          background: "linear-gradient(135deg, #00f5ff, #0080ff)",
          color: "#020817",
          fontWeight: "700",
          fontSize: "10px",
          letterSpacing: "1px",
          padding: "4px 10px",
          borderRadius: "4px",
          border: "none",
          cursor: "pointer",
        }}
      >
        RELOAD
      </button>
    </div>
  );
}
